import type { CSSProperties } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";
import { TextFieldView } from "@/components/molecules/form/TextFieldView";
import { EditorFieldWrapper } from "@/features/sparxsEditor/components/atoms/EditorFieldWrapper";
import { RichTextFieldView } from "@/components/molecules/form/RichTextFieldView";
import { useEditorFieldState } from "@/features/sparxsEditor/hooks/editor/useEditorFieldState";
import { EDITOR_EDITED_HIGHLIGHT_CLASS } from "@/features/sparxsEditor/components/atoms/editorConstants";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import type { InteractionBlueprintData } from "@/lib/contentParsers/interactionBlueprintSchema";
import type { ContentItem } from "@/lib/contentParsers/contentItemSchemas";
import type { RichTextEditorContentAreaVariant } from "@/features/sparxsEditor/components/organisms/richTextEditor/richTextEditorLayout";
import {
  isDynamicGridTemplateFieldPath,
  richTextVariablesForFieldPath,
} from "@/features/sparxsEditor/lib/form/richTextVariableScope";
import {
  RTE_FEATURE_BLACKLIST_COMPACT,
  RTE_FEATURE_BLACKLIST_MINIMAL,
  mergeFeatureBlacklists,
  type RteFeature,
} from "@/features/sparxsEditor/components/organisms/richTextEditor/richTextEditorFeatureBlacklist";

/** True when this field should use the Sparx rich (markdown-backed) editor. */
const isTextField = (name: string, label: string): boolean =>
  name.toLowerCase().endsWith(".text") || label.toLowerCase() === "text";

/** Image URL / alt under a dynamic grid cell template: match backend row-level {{column}} scope. */
const isDynamicGridImageUrlOrAltField = (name: string): boolean =>
  isDynamicGridTemplateFieldPath(name) &&
  (name.endsWith(".image.url") || name.endsWith(".image.altText"));

/** Single-line-tall rich area (same as CTA button text). */
const isDynamicGridImageUrlField = (name: string): boolean =>
  isDynamicGridTemplateFieldPath(name) && name.endsWith(".image.url");

/** Conditional CTA `display_message`: `…cta.messages.{index}` (see conditionalCtaView). */
const isDisplayMessageMessagesItemField = (name: string): boolean =>
  /\.messages\.\d+$/.test(name);

export interface EditorTextFieldViewProps<
  TFieldValues extends FieldValues = InteractionBlueprintData,
> {
  label: string;
  defaultValue?: string;
  id: string;

  control?: Control<TFieldValues>;
  name: string;
  style?: CSSProperties;
  modal?: boolean;
  toHide?: boolean;
  filled?: boolean;
  required?: boolean;
  errorMessage?: string;
  className?: string;
  disabled?: boolean;
  richTextContentAreaVariant?: RichTextEditorContentAreaVariant;
  /** Merged with field defaults (e.g. minimal RTE for dynamic grid image URL, compact for alt text). */
  richTextFeatureBlacklist?: readonly RteFeature[];
}

export const EditorTextFieldView = <
  TFieldValues extends FieldValues = InteractionBlueprintData,
>({
  label,
  id,
  control: controlProp,
  defaultValue = "",
  style,
  name,
  toHide = false,
  required,
  errorMessage,
  className,
  disabled = false,
  richTextContentAreaVariant = "default",
  richTextFeatureBlacklist,
  ...rest
}: EditorTextFieldViewProps<TFieldValues>) => {
  const { control, errorMessageToShow, isEdited, dispatchUpdateFormState } =
    useEditorFieldState<TFieldValues>(name, {
      control: controlProp,
      errorMessage,
    });

  const { field } = useController({
    name: name as FieldPath<TFieldValues>,
    control,
    defaultValue: defaultValue as Parameters<
      typeof useController<TFieldValues>
    >[0]["defaultValue"],
    rules: { required: required },
  });

  const form = useFormContext<TFieldValues>();
  const metadataVariables = form.watch(
    "metadata.variables" as FieldPath<TFieldValues>,
  );
  const contentRaw = form.watch("content" as FieldPath<TFieldValues>);
  const contentItems: ContentItem[] = Array.isArray(contentRaw)
    ? (contentRaw as ContentItem[])
    : [];

  const metaList: Variables[] = Array.isArray(metadataVariables)
    ? [...metadataVariables]
    : [];
  const variables: Variables[] = richTextVariablesForFieldPath(
    name,
    metaList,
    contentItems,
  );

  const useRichEditor =
    isTextField(name, label) ||
    isDynamicGridImageUrlOrAltField(name) ||
    isDisplayMessageMessagesItemField(name);
  const stringValue =
    typeof field.value === "string" ? field.value : String(field.value ?? "");

  const resolvedRichContentVariant: RichTextEditorContentAreaVariant =
    isDynamicGridImageUrlField(name) ? "cta" : richTextContentAreaVariant;

  const fieldDefaultRichTextBlacklist: readonly RteFeature[] | undefined =
    isDynamicGridImageUrlField(name)
      ? RTE_FEATURE_BLACKLIST_MINIMAL
      : isDynamicGridTemplateFieldPath(name) && name.endsWith(".image.altText")
        ? RTE_FEATURE_BLACKLIST_COMPACT
        : undefined;

  const resolvedRichTextFeatureBlacklist = mergeFeatureBlacklists(
    fieldDefaultRichTextBlacklist,
    richTextFeatureBlacklist,
  );

  return (
    <EditorFieldWrapper toHide={toHide} isEdited={isEdited}>
      {useRichEditor ? (
        <RichTextFieldView
          className={className}
          contentAreaVariant={resolvedRichContentVariant}
          disabled={disabled}
          errorMessage={errorMessageToShow}
          id={id}
          label={label}
          labelClassName={isEdited ? EDITOR_EDITED_HIGHLIGHT_CLASS : undefined}
          toHide={false}
          initialValue={stringValue}
          onChange={field.onChange}
          variables={variables}
          featureBlacklist={resolvedRichTextFeatureBlacklist}
          onBlur={() => {
            dispatchUpdateFormState(stringValue);
            field.onBlur();
          }}
        />
      ) : (
        <TextFieldView
          className={className}
          control={control}
          defaultValue={defaultValue}
          disabled={disabled}
          errorMessage={errorMessageToShow}
          id={id}
          label={label}
          labelClassName={isEdited ? EDITOR_EDITED_HIGHLIGHT_CLASS : undefined}
          name={name}
          required={required}
          style={style}
          toHide={false}
          onBlurWithValue={dispatchUpdateFormState}
          {...rest}
        />
      )}
    </EditorFieldWrapper>
  );
};
